import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Tag, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ARTICLE_CATEGORIES } from "@/components/concierge/categories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/concierge/articles`;

const CategorySelect = ({ value, onChange, testid }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)} className="h-9 rounded-md border px-2 text-sm bg-white" data-testid={testid}>
    {ARTICLE_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
  </select>
);

const ArticleRow = ({ article, headers, onChange }) => {
  const [price, setPrice] = useState(article.price);

  const save = async (changes) => {
    try {
      await axios.put(`${API}/${article.id}`, { name: article.name, price: article.price, category: article.category, ...changes }, { headers });
      toast.success("Article mis à jour");
      onChange();
    } catch (error) {
      toast.error(error.response?.data?.detail?.[0]?.msg || error.response?.data?.detail || "Erreur");
    }
  };

  const savePrice = () => {
    const p = parseFloat(price);
    if (!isNaN(p) && p !== article.price) save({ price: p });
  };

  const remove = async () => {
    if (!window.confirm(`Supprimer ${article.name} ?`)) return;
    await axios.delete(`${API}/${article.id}`, { headers });
    onChange();
  };

  return (
    <div className="flex items-center gap-2 border-b py-2 flex-wrap" data-testid={`concierge-article-${article.id}`}>
      <span className="flex-1 min-w-[140px]">{article.name}</span>
      <CategorySelect value={article.category || "Vêtements"} onChange={(category) => save({ category })} testid={`concierge-article-category-${article.id}`} />
      <Input type="number" step="0.01" className="w-24" value={price} onChange={(e) => setPrice(e.target.value)} onBlur={savePrice} data-testid={`concierge-article-price-${article.id}`} />
      <span>€</span>
      <Button variant="ghost" size="icon" className="text-red-500" onClick={remove} data-testid={`concierge-article-delete-${article.id}`}><Trash2 className="w-4 h-4" /></Button>
    </div>
  );
};

export const ConciergeArticles = ({ headers }) => {
  const [articles, setArticles] = useState([]);
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [category, setCategory] = useState(ARTICLE_CATEGORIES[0]);

  const load = useCallback(async () => {
    const res = await axios.get(API, { headers });
    setArticles(res.data);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const add = async (e) => {
    e.preventDefault();
    try {
      await axios.post(API, { name, price: parseFloat(price), category }, { headers });
      setName(""); setPrice("");
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div className="bg-white rounded-xl border p-4 space-y-3" data-testid="concierge-articles">
      <h2 className="font-bold flex items-center gap-2"><Tag className="w-5 h-5 text-pink-600" />Articles et prix fixes</h2>
      <form onSubmit={add} className="flex gap-2 flex-wrap">
        <Input placeholder="Article (ex : Chemise)" className="flex-1 min-w-[160px]" value={name} onChange={(e) => setName(e.target.value)} required data-testid="concierge-article-name-input" />
        <CategorySelect value={category} onChange={setCategory} testid="concierge-article-category-input" />
        <Input type="number" step="0.01" min="0" placeholder="Prix €" className="w-28" value={price} onChange={(e) => setPrice(e.target.value)} required data-testid="concierge-article-price-input" />
        <Button type="submit" className="bg-pink-600 hover:bg-pink-700" data-testid="concierge-article-add-btn"><Plus className="w-4 h-4" /></Button>
      </form>
      <div>{articles.map((a) => <ArticleRow key={`${a.id}-${a.price}-${a.category}`} article={a} headers={headers} onChange={load} />)}</div>
      {articles.length === 0 && <p className="text-sm text-slate-400">Aucun article : la conciergerie ne pourra pas faire de bon.</p>}
    </div>
  );
};
